import { Message } from '@langchain/langgraph-sdk'
import React, { useState } from 'react'
import { useStreamContext } from '../../../providers/Stream'
import { Textarea } from '../../ui/textarea'
import { getContentString } from '../utils'
import { BranchSwitcher, CommandBar } from './Shared'

function EditableContent({
    value,
    setValue,
    onSubmit
}: {
    value: string
    setValue: React.Dispatch<React.SetStateAction<string>>
    onSubmit: () => void
}) {
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault()
            onSubmit()
        }
    }

    return (
        <Textarea value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={handleKeyDown} style={{ outline: 'none', boxShadow: 'none' }} />
    )
}

export function HumanMessage({ message, isLoading }: { message: Message; isLoading: boolean }) {
    const thread = useStreamContext()
    const meta = thread.getMessagesMetadata(message)
    const parentCheckpoint = meta?.firstSeenState?.parent_checkpoint

    const [isEditing, setIsEditing] = useState(false)
    const [value, setValue] = useState('')
    const contentString = getContentString(message.content)
    const [isHovering, setIsHovering] = useState(false)

    const handleSubmitEdit = () => {
        setIsEditing(false)

        const newMessage: Message = { type: 'human', content: value }
        thread.submit(
            { messages: [newMessage] },
            {
                checkpoint: parentCheckpoint,
                streamMode: ['values'],
                optimisticValues: (prev) => {
                    const values = meta?.firstSeenState?.values
                    if (!values) return prev

                    return {
                        ...values,
                        messages: [...(values.messages ?? []), newMessage]
                    }
                }
            }
        )
    }

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginLeft: 'auto',
                width: isEditing ? '100%' : 'auto',
                maxWidth: isEditing ? '36rem' : 'auto'
            }}
            onMouseOver={() => setIsHovering(true)}
            onMouseOut={() => setIsHovering(false)}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0px',
                    width: isEditing ? '100%' : 'auto'
                }}
            >
                {isEditing ? (
                    <EditableContent value={value} setValue={setValue} onSubmit={handleSubmitEdit} />
                ) : (
                    <p
                        style={{
                            marginLeft: 'auto',
                            width: 'fit-content',
                            backgroundColor: 'var(--muted)',
                            borderRadius: '1.5rem',
                            padding: '0.5rem 1rem',
                            whiteSpace: 'pre-wrap'
                        }}
                    >
                        {contentString}
                    </p>
                )}

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginLeft: 'auto',
                        opacity: isEditing || isHovering ? 1 : 0,
                        transition: 'opacity 0.2s ease'
                    }}
                >
                    <BranchSwitcher
                        branch={meta?.branch}
                        branchOptions={meta?.branchOptions}
                        onSelect={(branch) => thread.setBranch(branch)}
                        isLoading={isLoading}
                    />
                    <CommandBar
                        isLoading={isLoading}
                        content={contentString}
                        isEditing={isEditing}
                        setIsEditing={(c) => {
                            if (c) {
                                setValue(contentString)
                            }
                            setIsEditing(c)
                        }}
                        handleSubmitEdit={handleSubmitEdit}
                        isHumanMessage={true}
                    />
                </div>
            </div>
        </div>
    )
}
